import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
// Assuming API_BASE_URL is defined elsewhere and is accessible,
// or could be passed as a prop if this component is not the root of the app.
// const API_BASE_URL = 'http://localhost:8000/api'; // Example if not from App.tsx
import { API_BASE_URL } from '../App';
// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, CircularProgress, Tooltip, Snackbar, Alert,
    Chip, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText,
    InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// --- Icons ---
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PolicyIcon from '@mui/icons-material/Policy';
import DnsIcon from '@mui/icons-material/Dns';

// --- Interfaces ---
interface TrackerDomain {
    id: number;
    domain_url: string;
    status: 'active' | 'inactive';
    // Changed health_status to string | null to accommodate any string from the API.
    health_status: string | null;
    created_at: string;
    updated_at: string;
}

interface HealthCheckResult {
    status: 'clean' | 'banned' | 'suspicious';
    summary: string;
    details: {
        google: { threats: string[] };
        virustotal: { malicious: number; suspicious: number };
    };
}



// --- AUXILIARY COMPONENTS ---

/**
 * Component for the domain creation and editing form.
 */
const DomainForm: React.FC<{
    newDomainUrl: string;
    setNewDomainUrl: React.Dispatch<React.SetStateAction<string>>;
    newDomainStatus: 'active' | 'inactive';
    setNewDomainStatus: React.Dispatch<React.SetStateAction<'active' | 'inactive'>>;
    handleSubmit: (event: React.FormEvent) => void;
    editingDomainId: number | null;
    cancelEditing: () => void;
    formError: string | null;
    isSubmitting: boolean; // New prop for submission status
    hasSubmitted: boolean; // NUEVO: Prop para saber si se ha intentado enviar
}> = ({ newDomainUrl, setNewDomainUrl, newDomainStatus, setNewDomainStatus, handleSubmit, editingDomainId, cancelEditing, formError, isSubmitting, hasSubmitted }) => {
    // Simple regex for domain format validation (without http/https)
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/;
    
    // Function to validate URL format
    const isValidUrl = (url: string) => {
        const cleanUrl = url.replace(/https?:\/\//, '');
        return domainRegex.test(cleanUrl);
    };

    const urlError = newDomainUrl.trim() && !isValidUrl(newDomainUrl.trim()) ? "Formato de dominio inválido (ej. tudominio.com)" : "";
    const isUrlEmpty = !newDomainUrl.trim();

    return (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
            <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                {editingDomainId ? 'Editar Dominio' : 'Crear Nuevo Dominio'}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={3}>
                    <TextField
                        name="domain_url"
                        label="Dominio (ej. tudominio.com)"
                        value={newDomainUrl}
                        onChange={(e) => setNewDomainUrl(e.target.value)}
                        required
                        fullWidth
                        // CAMBIO: La validación de campo vacío solo se activa si hasSubmitted es true
                        error={!!formError || (hasSubmitted && isUrlEmpty) || !!urlError}
                        helperText={(hasSubmitted && isUrlEmpty) ? "El dominio es obligatorio." : urlError || formError}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    https://
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Estado</InputLabel>
                        <Select
                            name="status"
                            value={newDomainStatus}
                            label="Estado"
                            onChange={(e) => setNewDomainStatus(e.target.value as 'active' | 'inactive')}
                        >
                            <MenuItem value="active">Activo</MenuItem>
                            <MenuItem value="inactive">Inactivo</MenuItem>
                        </Select>
                    </FormControl>
                    <Stack direction="row" spacing={2}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || !!urlError} // Deshabilitado si hay error de formato
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutlineIcon />}
                        >
                            {editingDomainId ? 'Actualizar Dominio' : 'Crear Dominio'}
                        </Button>
                        {editingDomainId && (
                            <Button type="button" variant="text" onClick={cancelEditing} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    );
};

/**
 * Component to display the domain health status as a Chip.
 * Now handles unknown string statuses from the API more flexibly.
 */
const HealthStatusChip: React.FC<{ status: string | null }> = ({ status }) => {
    // Define a map for known statuses, including their labels and colors
    const statusMap: { [key: string]: { label: string; color: 'success' | 'error' | 'warning' | 'default' } } = {
        clean: { label: 'Limpio', color: 'success' },
        banned: { label: 'Baneado', color: 'error' },
        suspicious: { label: 'Sospechoso', color: 'warning' },
    };

    // If status is null, show "No revisado"
    if (status === null) {
        return <Chip label="No revisado" color="default" size="small" variant="outlined" />;
    }

    // If status is a known value in the map, use its defined label and color
    if (statusMap[status]) {
        const currentStatus = statusMap[status];
        return <Chip label={currentStatus.label} color={currentStatus.color} size="small" variant="outlined" />;
    }

    // If status is a string but not in the map, display the raw status string
    // You could choose a default color like 'info' or 'primary' for unknown statuses
    return <Chip label={status} color="info" size="small" variant="outlined" />;
};

/**
 * Component to display the domains table.
 */
const DomainsTable: React.FC<{
    domains: TrackerDomain[];
    startEditing: (domain: TrackerDomain) => void;
    onDeleteConfirm: (id: number) => void;
    // onCheckHealth and checkingHealthId are kept because the button for health check remains.
    onCheckHealth: (domain: TrackerDomain) => void;
    checkingHealthId: number | null;
}> = ({ domains, startEditing, onDeleteConfirm, onCheckHealth, checkingHealthId }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    if (domains.length === 0) {
        return <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No hay dominios registrados que coincidan con la búsqueda.</Typography>;
    }

    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px' }}>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>ID</TableCell>
                            <TableCell sx={headerCellStyle}>Dominio URL</TableCell>
                            <TableCell sx={headerCellStyle}>Estado</TableCell>
                            {/* Removed Health column header */}
                            <TableCell sx={headerCellStyle}>Creado</TableCell>
                            <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {domains.map((domain) => (
                            <TableRow key={domain.id} hover>
                                <TableCell>{domain.id}</TableCell>
                                <TableCell>
                                    {/* Ensure the link is always https to prevent Mixed Content issues */}
                                    <Tooltip title={`Ir a ${domain.domain_url}`}>
                                        <Typography
                                            component="a"
                                            href={`https://${domain.domain_url.replace(/https?:\/\//, '')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: theme.palette.primary.main, textDecoration: 'none', wordBreak: 'break-all' }}
                                        >
                                            {domain.domain_url.replace(/https?:\/\//, '')}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={domain.status === 'active' ? 'Activo' : 'Inactivo'}
                                        color={domain.status === 'active' ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                {/* Removed Health status cell */}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(domain.created_at).toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Revisar Salud">
                                        <IconButton size="small" onClick={() => onCheckHealth(domain)} color="info" disabled={checkingHealthId === domain.id}>
                                            <PolicyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar Dominio">
                                        <IconButton size="small" onClick={() => startEditing(domain)} color="primary">
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar Dominio">
                                        <IconButton size="small" onClick={() => onDeleteConfirm(domain.id)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

/**
 * Generic component for confirmation dialogs.
 */
const ConfirmationDialog: React.FC<{ open: boolean; onClose: () => void; onConfirm: () => void; title: string; children: React.ReactNode; }> = ({ open, onClose, onConfirm, title, children }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle color="text.primary">{title}</DialogTitle>
        <DialogContent><DialogContentText color="text.secondary">{children}</DialogContentText></DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} color="error" variant="contained">Confirmar</Button>
        </DialogActions>
    </Dialog>
);

/**
 * Component to display health check results.
 */
const HealthCheckDialog: React.FC<{ open: boolean; onClose: () => void; result: HealthCheckResult | null }> = ({ open, onClose, result }) => {
    if (!result) return null; // Do not render if no results

    const { status, summary, details } = result;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle color="text.primary">Resultado de la Verificación de Salud</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {/* HealthStatusChip is still used here for the dialog display */}
                    <HealthStatusChip status={status} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} color="text.secondary">{summary}</Typography>

                    <Box>
                        <Typography variant="h6" gutterBottom color="text.primary">Google Safe Browse</Typography>
                        {details.google.threats.length > 0 ? (
                            // Show all threats separated by commas.
                            <Chip label={details.google.threats.join(', ')} color="error" />
                        ) : (
                            <Typography color="text.secondary">No se encontraron amenazas.</Typography>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom color="text.primary">VirusTotal</Typography>
                        <Stack direction="row" spacing={2}>
                            <Chip label={`${details.virustotal.malicious} Maliciosos`} color={details.virustotal.malicious > 0 ? 'error' : 'default'} />
                            <Chip label={`${details.virustotal.suspicious} Sospechosos`} color={details.virustotal.suspicious > 0 ? 'warning' : 'default'} />
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};


// --- MAIN COMPONENT ---
const DomainsPage: React.FC = () => {
    // --- STATES ---
    const [domains, setDomains] = useState<TrackerDomain[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true); // Initial state true for first load
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // New state for form submission
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [newDomainUrl, setNewDomainUrl] = useState<string>('');
    const [newDomainStatus, setNewDomainStatus] = useState<'active' | 'inactive'>('active');
    const [editingDomainId, setEditingDomainId] = useState<number | null>(null);
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, id: number | null }>({ open: false, id: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false); // NUEVO ESTADO para controlar el envío

    // --- New states for health check (kept as they are still used by the dialog and button) ---
    const [checkingHealthId, setCheckingHealthId] = useState<number | null>(null);
    const [healthResult, setHealthResult] = useState<HealthCheckResult | null>(null);
    const [isHealthDialogOpen, setIsHealthDialogOpen] = useState(false);

    // --- FUNCTIONS ---

    /**
     * Function to fetch domains from the API.
     * Uses useCallback to memoize the function and avoid recreating it on every render.
     */
    const fetchDomains = useCallback(async () => {
        setIsLoading(true); // Always set to true when starting load
        setError(null); // Clear previous errors
        try {
            const response = await axios.get(`${API_BASE_URL}/tracker-domains`);
            setDomains(response.data);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Error al obtener dominios.';
            setError(errorMessage);
            setNotification({ type: 'error', message: errorMessage });
        } finally {
            setIsLoading(false); // Always set to false when loading finishes (success or error)
        }
    }, []); // Empty dependencies: this function is created only once.

    /**
     * Effect to load domains on component mount.
     */
    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]); // Dependency on fetchDomains

    /**
     * Handler for creating or updating domains.
     * Uses useCallback to memoize the function.
     */
    const handleSubmit = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        setNotification(null); // Clear previous notifications
        setHasSubmitted(true); // Marcar que se ha intentado enviar
        setIsSubmitting(true);

        if (!newDomainUrl.trim()) {
            // No es necesario setNotification aquí, el helperText del formulario se encargará
            setIsSubmitting(false);
            return;
        }

        // Simple domain format validation
        const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/;
        const cleanUrlForValidation = newDomainUrl.trim().replace(/https?:\/\//, '');
        if (!domainRegex.test(cleanUrlForValidation)) {
            // No es necesario setNotification aquí, el helperText se encargará
            setIsSubmitting(false);
            return;
        }

        // Ensure the URL has the https:// prefix for API consistency
        let fullDomainUrl = newDomainUrl.trim();
        if (!fullDomainUrl.startsWith('https://') && !fullDomainUrl.startsWith('http://')) {
            fullDomainUrl = `https://${fullDomainUrl}`;
        }

        const domainData = { domain_url: fullDomainUrl, status: newDomainStatus };
        const apiEndpoint = editingDomainId ? `/tracker-domains/${editingDomainId}` : '/tracker-domains';
        const method = editingDomainId ? 'PUT' : 'POST';

        try {
            await axios({ method, url: `${API_BASE_URL}${apiEndpoint}`, data: domainData });
            setNewDomainUrl(''); // Clear field
            setNewDomainStatus('active'); // Reset status
            setEditingDomainId(null); // Exit edit mode
            setHasSubmitted(false); // Resetear el estado de envío
            setNotification({ type: 'success', message: `Dominio ${editingDomainId ? 'actualizado' : 'creado'} con éxito!` });
            fetchDomains(); // Reload the domain list
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al guardar el dominio.' });
        } finally {
            setIsSubmitting(false); // End submission state
        }
    }, [newDomainUrl, newDomainStatus, editingDomainId, fetchDomains]); // Dependencies: states used in the function

    /**
     * Handler for deleting domains.
     * Uses useCallback to memoize the function.
     */
    const handleDelete = useCallback(async () => {
        if (!deleteDialog.id) return; // Do nothing if no ID to delete
        try {
            await axios.delete(`${API_BASE_URL}/tracker-domains/${deleteDialog.id}`);
            setNotification({ type: 'success', message: `Dominio (ID: ${deleteDialog.id}) eliminado.` });
            fetchDomains(); // Reload the domain list
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al eliminar el dominio.' });
        } finally {
            setDeleteDialog({ open: false, id: null }); // Close delete dialog
        }
    }, [deleteDialog.id, fetchDomains]); // Dependencies: dialog ID and fetchDomains

    /**
     * Starts edit mode, preloading domain data into the form.
     * Uses useCallback to memoize the function.
     */
    const startEditing = useCallback((domain: TrackerDomain) => {
        setEditingDomainId(domain.id);
        // Remove https:// or http:// so the user only edits the domain
        setNewDomainUrl(domain.domain_url.replace(/https?:\/\//, ''));
        setNewDomainStatus(domain.status);
        setHasSubmitted(false); // Resetear al empezar a editar
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top to see the form
    }, []); // Empty dependencies: created only once.

    /**
     * Cancels edit mode and clears the form.
     * Uses useCallback to memoize the function.
     */
    const cancelEditing = useCallback(() => {
        setEditingDomainId(null);
        setNewDomainUrl('');
        setNewDomainStatus('active');
        setHasSubmitted(false); // Resetear al cancelar
    }, []); // Empty dependencies: created only once.

    /**
     * Checks the health of a domain using the external API.
     * Uses useCallback to memoize the function.
     */
    const handleCheckHealth = useCallback(async (domain: TrackerDomain) => {
        setCheckingHealthId(domain.id); // Indicate which domain is being checked
        setNotification(null); // Clear previous notifications
        try {
            const response = await axios.post(`${API_BASE_URL}/health/check-domain`, {
                urlToCheck: domain.domain_url,
                entityId: domain.id,
                entityType: 'tracker_domain'
            });
            setHealthResult(response.data); // Save the check result
            setIsHealthDialogOpen(true); // Open the results dialog
            fetchDomains(); // Optional: Reload domains to reflect the new health status
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al revisar la salud del dominio.' });
        } finally {
            setCheckingHealthId(null); // Reset checking status
        }
    }, [fetchDomains]); // Dependency on fetchDomains

    /**
     * Filters domains based on the search term.
     * Uses useMemo to recalculate the filtered list only when `domains` or `searchTerm` change.
     */
    const filteredDomains = useMemo(() => {
        if (!searchTerm) return domains; // If no search term, return all domains
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return domains.filter(domain =>
            String(domain.id).includes(lowerCaseSearchTerm) || // Search by ID
            domain.domain_url.toLowerCase().includes(lowerCaseSearchTerm) || // Search by URL
            domain.status.toLowerCase().includes(lowerCaseSearchTerm) // Removed health_status from search filter
        );
    }, [domains, searchTerm]); // Dependencies: domains list and search term

    // --- RENDER ---

    // Show initial loading spinner if no domains are loaded yet
    if (isLoading && domains.length === 0) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary" sx={{ ml: 2 }}>Cargando dominios...</Typography>
            </Container>
        );
    }

    // Show error message if initial load failed and no domains
    if (error && domains.length === 0 && !isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Alert severity="error">Error al cargar dominios: {error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, fontFamily: 'Inter, sans-serif' }}>
            {/* Snackbar for success/error notifications */}
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            {/* Confirmation dialog for deletion */}
            <ConfirmationDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null })}
                onConfirm={handleDelete}
                title="Confirmar Eliminación"
            >
                ¿Estás seguro de que quieres eliminar este dominio? Esta acción es irreversible.
            </ConfirmationDialog>

            {/* Dialog to display health check results */}
            <HealthCheckDialog
                open={isHealthDialogOpen}
                onClose={() => setIsHealthDialogOpen(false)}
                result={healthResult}
            />

            {/* Main page title */}
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <DnsIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Gestión de Dominios del Tracker
            </Typography>

            {/* Form to create/edit domains */}
            <DomainForm
                newDomainUrl={newDomainUrl} setNewDomainUrl={setNewDomainUrl}
                newDomainStatus={newDomainStatus} setNewDomainStatus={setNewDomainStatus}
                handleSubmit={handleSubmit}
                editingDomainId={editingDomainId} cancelEditing={cancelEditing}
                formError={notification?.type === 'error' ? notification.message : null}
                isSubmitting={isSubmitting}
                hasSubmitted={hasSubmitted} // Pasar el nuevo estado al formulario
            />

            {/* Registered domains section with search bar */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h5" component="h2" color="text.primary" sx={{ fontWeight: 'bold' }}>
                    Dominios Registrados
                </Typography>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                    sx={{ minWidth: { xs: '100%', sm: '250px' } }}
                />
            </Stack>

            {/* Domains table */}
            <DomainsTable
                domains={filteredDomains}
                startEditing={startEditing}
                onDeleteConfirm={(id) => setDeleteDialog({ open: true, id })}
                onCheckHealth={handleCheckHealth}
                checkingHealthId={checkingHealthId}
            />
        </Container>
    );
};

export default DomainsPage;